/**
 * XRInputPollSystem — Polls XR controllers/hands and writes to InputReader.
 *
 * Phase: Input, SubPhase: Update, Priority: 5
 * Runs after the engine's InputSystem (priority 0) so XR input
 * supplements or overrides keyboard/mouse/gamepad input.
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EngineRenderer } from '../../render/Renderer.js';
import type { XRSessionState } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { applyRadialDeadzone } from '../../shared/math.js';
import { HandGestureRecognizer, type JointPose } from '../../input/HandGestureRecognizer.js';
import { VisionProInput } from '../../input/VisionProInput.js';
import { NeedsSync } from '../../ecs/Component.js';
import { Logger } from '../../core/Logger.js';

/** W3C joint names in order (25 joints) — hoisted to avoid per-frame allocation. */
const JOINT_NAMES: readonly string[] = [
  'wrist',
  'thumb-metacarpal', 'thumb-phalanx-proximal', 'thumb-phalanx-distal', 'thumb-tip',
  'index-finger-metacarpal', 'index-finger-phalanx-proximal', 'index-finger-phalanx-intermediate', 'index-finger-phalanx-distal', 'index-finger-tip',
  'middle-finger-metacarpal', 'middle-finger-phalanx-proximal', 'middle-finger-phalanx-intermediate', 'middle-finger-phalanx-distal', 'middle-finger-tip',
  'ring-finger-metacarpal', 'ring-finger-phalanx-proximal', 'ring-finger-phalanx-intermediate', 'ring-finger-phalanx-distal', 'ring-finger-tip',
  'pinky-finger-metacarpal', 'pinky-finger-phalanx-proximal', 'pinky-finger-phalanx-intermediate', 'pinky-finger-phalanx-distal', 'pinky-finger-tip',
] as const;

/** Pre-allocated scratch joint arrays — one per hand (left=0, right=1). */
const _jointScratch: [JointPose[], JointPose[]] = [
  Array.from({ length: 25 }, () => ({ position: { x: 0, y: 0, z: 0 } })),
  Array.from({ length: 25 }, () => ({ position: { x: 0, y: 0, z: 0 } })),
];

/**
 * XR button index → action name pairs for synthetic release on disconnect.
 * INVARIANT: Every button index passed to _edgeDetect() in the polling loop
 * below MUST appear here so _flushXRReleases emits releases on disconnect.
 * Currently tracked indices: 0 (trigger), 1 (grip), 3 (thumbstick), 4 (A/X), 5 (B/Y).
 * Button 2 (touchpad) is intentionally unmapped.
 */
const XR_BUTTON_ACTIONS: ReadonlyArray<[number, string, string]> = [
  [0, 'XR_Trigger_Left',          'XR_Trigger_Right'],
  [1, 'XR_Grip_Left',             'XR_Grip_Right'],
  [3, 'XR_Thumbstick_Left_Press', 'XR_Thumbstick_Right_Press'],
  [4, 'XR_X_Button',              'XR_A_Button'],
  [5, 'XR_Y_Button',              'XR_B_Button'],
];

/**
 * Per-hand action-name tables, hoisted so the 90 Hz poll never rebuilds
 * `XR_*_${suffix}` template strings (2 hands × 8 names × 90 Hz of short-lived
 * strings per second under the old closure-based poll).
 */
interface HandActionNames {
  trigger: string;
  triggerValue: string;
  grip: string;
  gripValue: string;
  thumbstick: string;
  thumbstickPress: string;
  primary: string;
  secondary: string;
}
const LEFT_ACTIONS: HandActionNames = {
  trigger: 'XR_Trigger_Left', triggerValue: 'XR_Trigger_Left_Value',
  grip: 'XR_Grip_Left', gripValue: 'XR_Grip_Left_Value',
  thumbstick: 'XR_Thumbstick_Left', thumbstickPress: 'XR_Thumbstick_Left_Press',
  primary: 'XR_X_Button', secondary: 'XR_Y_Button',
};
const RIGHT_ACTIONS: HandActionNames = {
  trigger: 'XR_Trigger_Right', triggerValue: 'XR_Trigger_Right_Value',
  grip: 'XR_Grip_Right', gripValue: 'XR_Grip_Right_Value',
  thumbstick: 'XR_Thumbstick_Right', thumbstickPress: 'XR_Thumbstick_Right_Press',
  primary: 'XR_A_Button', secondary: 'XR_B_Button',
};

/** Shared immutable empty list — the not-presenting path used to allocate `[]` every frame. */
const NO_SOURCES: readonly unknown[] = Object.freeze([]);

/** Shape of three's per-joint groups on `renderer.xr.getHand(i).joints`. */
interface ThreeHandJoint {
  position: { x: number; y: number; z: number };
  visible: boolean;
}

/** Emit _setReleased for any XR buttons that were held when a controller disappeared. */
function _flushXRReleases(prev: boolean[], suffix: 'Left' | 'Right', reader: any): void {
  const col = suffix === 'Left' ? 1 : 2;
  for (const entry of XR_BUTTON_ACTIONS) {
    if (prev[entry[0]]) reader._setReleased(entry[col]);
  }
}

export class XRInputPollSystem implements ISystem {
  readonly name = 'XRInputPoll';
  readonly phase = LoopPhase.Input;
  readonly subPhase = SubPhase.Update;
  readonly priority = 5;

  private _state: XRSessionState;
  private _renderer: EngineRenderer;

  readonly gestureRecognizer = new HandGestureRecognizer();
  readonly visionProInput = new VisionProInput();

  /** Previous-frame XR button states per hand for edge detection. key: 'left'|'right' */
  private _prevXRButtons = new Map<string, boolean[]>();
  /** Scratch object for radial deadzone output. */
  private _scratch = { x: 0, y: 0 };
  /** Track per-hand tracking presence for loss detection. [left, right] */
  private _handWasTracked: [boolean, boolean] = [false, false];
  /** Sticky flag: once a transient-pointer (Vision Pro pinch) is detected, stays
   *  true until session ends so DeviceDetector's 2-frame hysteresis can latch. */
  private _visionProDetected = false;
  /** Cached InputSystem reference to avoid linear scan every frame. */
  private _inputSystemCache: any = null;
  /** One-shot warning so missing __xrFrame is visible in devtools. */
  private _warnedMissingFrame = false;
  /**
   * True once a presenting frame ran; the not-presenting branch is a one-shot
   * teardown gated on it instead of re-running (and allocating) every frame
   * for the whole non-XR lifetime of the app.
   */
  private _wasPresenting = false;

  constructor(state: XRSessionState, renderer: EngineRenderer) {
    this._state = state;
    this._renderer = renderer;
  }

  execute(_dt: number, world: World): void {
    if (!this._state.isPresenting) {
      if (!this._wasPresenting) return;
      this._wasPresenting = false;

      const inputSystem = this._findInputSystem(world);
      const reader = inputSystem && (inputSystem as any).reader;
      // Release buttons still held at session end (trigger held while taking
      // the headset off kept XR_Trigger_* in reader.held forever), then clear.
      if (this._prevXRButtons.size > 0) {
        if (reader) {
          const l = this._prevXRButtons.get('left'); if (l) _flushXRReleases(l, 'Left', reader);
          const r = this._prevXRButtons.get('right'); if (r) _flushXRReleases(r, 'Right', reader);
        }
        this._prevXRButtons.clear();
      }
      (inputSystem as { haptics?: { setXRInputSources?(s: Iterable<unknown>): void } } | null)?.haptics?.setXRInputSources?.(NO_SOURCES);

      // Always clear XR device flags and VisionPro state — these must reset
      // regardless of whether hand tracking was used (controller-only sessions).
      if (reader) {
        if (typeof reader._setXRActive === 'function') reader._setXRActive(false);
        if (typeof reader._setVisionProActive === 'function') reader._setVisionProActive(false);
      }
      this._visionProDetected = false;
      // Release VP_Select if mid-pinch at session end — dispose() clears internal
      // state but doesn't emit the release, leaving held set permanently stuck.
      if (reader && this.visionProInput.isTransientPointerActive()) {
        reader._setReleased('VP_Select');
      }
      this.visionProInput.dispose();

      // Flush any held gestures so actions don't stay stuck between sessions
      if (this._handWasTracked[0] || this._handWasTracked[1]) {
        if (reader) {
          if (this._handWasTracked[0]) this.gestureRecognizer.forceReleaseHand(0, reader);
          if (this._handWasTracked[1]) this.gestureRecognizer.forceReleaseHand(1, reader);
        }
        this._handWasTracked[0] = false;
        this._handWasTracked[1] = false;
      }
      return;
    }
    this._wasPresenting = true;

    const session = this._state.session!;
    // Use __xrFrame (set by App.ts animation loop) for consistency with all
    // other XR systems (HitTest, Anchor, Depth, LightProbe).
    const frame = (globalThis as any).__xrFrame as XRFrame | undefined;

    // Teleport/recenter (`renderer.xr.setReferenceSpace(offsetSpace)`, as the
    // mixed-reality template does) swaps three's live space, but state.referenceSpace
    // was captured once at sessionstart — hit-test, anchors, planes, Vision Pro gaze
    // and the Lua joint API kept resolving poses in the pre-teleport space while the
    // cameras/controllers had moved. Mirror three's space here, first thing each
    // frame (Input phase runs before every other XR system). Guarded on three's own
    // isPresenting: the experimental WebGPU binding never calls setSession, so
    // three's cached space would be a previous session's dead one.
    const nativeXr = this._renderer.native?.xr as
      | { isPresenting?: boolean; getReferenceSpace?(): XRReferenceSpace | null; _controllerInputSources?: (XRInputSource | null)[] }
      | undefined;
    if (nativeXr?.isPresenting && typeof nativeXr.getReferenceSpace === 'function') {
      const live = nativeXr.getReferenceSpace();
      if (live && live !== this._state.referenceSpace) this._state.referenceSpace = live;
    }

    const refSpace = this._state.referenceSpace;
    if (!frame || !refSpace) {
      if (!frame && !this._warnedMissingFrame) {
        Logger.warn('XRInputPoll', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    // Find InputReader from the InputSystem
    const inputSystem = this._findInputSystem(world);
    const sources = session.inputSources;
    // HapticsManager's doc says this system feeds it — nothing ever did, so
    // every playXRHaptic() silently no-op'd. Hand over the live
    // XRInputSourceArray itself (iterable, [SameObject] per spec) instead of
    // Array.from()-copying it every frame.
    (inputSystem as { haptics?: { setXRInputSources?(s: Iterable<unknown>): void } } | null)?.haptics?.setXRInputSources?.(sources);
    if (!inputSystem) return;
    const reader = (inputSystem as any).reader;
    if (!reader || typeof reader._setPressed !== 'function') return;

    // Signal XR presence to DeviceDetector via InputReader
    if (typeof reader._setXRActive === 'function') reader._setXRActive(true);

    // Track which hands were seen this frame to clean up stale prev-state
    // when a controller disconnects mid-session (isPresenting stays true).
    let seenLeft = false;
    let seenRight = false;

    // Hand-joint results for the gesture recognizer (null = hand not tracked).
    let leftJoints: JointPose[] | null = null;
    let rightJoints: JointPose[] | null = null;

    // Single index-based pass (no iterator allocation) over the live source list.
    const slotTable = nativeXr?._controllerInputSources;
    for (let s = 0; s < sources.length; s++) {
      const inputSource = sources[s];
      const hand = inputSource.handedness;
      if (hand === 'none') continue;
      const isLeft = hand === 'left';
      if (isLeft) seenLeft = true;
      else seenRight = true;

      const actions = isLeft ? LEFT_ACTIONS : RIGHT_ACTIONS;

      // three assigns controller slots first-come in onInputSourcesChange, NOT
      // by handedness: a lone right controller lands in slot 0. Assuming
      // slot = handedness left the slot-0 entity (the one three actually drives)
      // frozen at the origin and wrote grip 1's dead pose onto entity 1. Resolve
      // the slot from three's own table; fall back to the handedness convention
      // when three isn't driving controllers (WebGPU binding / pre-connect frame).
      let slot = slotTable ? slotTable.indexOf(inputSource) : -1;
      if (slot < 0) slot = isLeft ? 0 : 1;
      const gripGroup = this._state.controllerGripGroups.get(slot);

      // Update entity transform from Three.js group (auto-updated by WebXRManager).
      // Tag NeedsSync so downstream systems (physics kinematics, AI, Lua
      // scripts reading the controller pose) see the fresh transform on
      // the next tick rather than a zero-initialised Transform from
      // controller-entity creation.
      const entityId = this._state.controllerEntities.get(slot);
      if (entityId !== undefined && gripGroup) {
        try {
          const obj = world.getObject3D(entityId);
          obj.position.copy(gripGroup.position);
          obj.quaternion.copy(gripGroup.quaternion);
          world.addTag(entityId, NeedsSync);
        } catch {
          // Entity may have been destroyed during session teardown — silently skip
        }
      }

      // Poll gamepad (controllers with xr-standard mapping)
      const gp = inputSource.gamepad;
      if (gp) {
        // Ensure prev-state array exists for this hand
        let prev = this._prevXRButtons.get(hand);
        if (!prev) {
          prev = [];
          this._prevXRButtons.set(hand, prev);
        }
        while (prev.length < gp.buttons.length) prev.push(false);
        // Shrink if gamepad reports fewer buttons (profile change) —
        // prevents stale `true` entries from causing phantom releases.
        if (prev.length > gp.buttons.length) prev.length = gp.buttons.length;

        // Trigger (button 0, analog)
        this._edgeDetect(reader, prev, gp, 0, actions.trigger);
        reader._setAxis(actions.triggerValue, gp.buttons[0]?.value ?? 0);

        // Grip (button 1, analog)
        this._edgeDetect(reader, prev, gp, 1, actions.grip);
        reader._setAxis(actions.gripValue, gp.buttons[1]?.value ?? 0);

        // A/X button (button 4) and B/Y button (button 5)
        this._edgeDetect(reader, prev, gp, 4, actions.primary);
        this._edgeDetect(reader, prev, gp, 5, actions.secondary);

        // Thumbstick (axes 2-3) — radial deadzone consistent with GamepadManager
        // Invert Y so up = positive (matching GamepadManager convention)
        const rawX = gp.axes[2] ?? 0;
        const rawY = gp.axes[3] ?? 0;
        applyRadialDeadzone(rawX, -rawY, 0.15, 0.95, this._scratch);
        reader._setAxis2D(actions.thumbstick, this._scratch.x, this._scratch.y);

        // Thumbstick press (button 3)
        this._edgeDetect(reader, prev, gp, 3, actions.thumbstickPress);
      }

      // ─── Hand joints ────────────────────────────────────────────────────
      const xrHand = (inputSource as any).hand;
      if (!xrHand) continue;

      // Reuse pre-allocated scratch array — update positions in-place
      const joints = isLeft ? _jointScratch[0] : _jointScratch[1];
      // three's WebXRController.update() already resolved every joint pose for
      // this frame (same XRFrame, same reference space) into
      // renderer.xr.getHand(slot).joints — re-querying frame.getJointPose 25×
      // per hand doubled the UA work and allocated ~150–250 pose/transform/point
      // objects per frame. Read three's result; fall back to getJointPose only
      // where three isn't driving the hand (WebGPU binding / joint not created yet).
      const handGroup = this._state.handGroups.get(slot) as
        | ({ joints?: Record<string, ThreeHandJoint | undefined> } | undefined);
      const threeJoints = handGroup?.joints;

      let validCount = 0;
      for (let i = 0; i < JOINT_NAMES.length; i++) {
        const pos = joints[i].position;
        const tj = threeJoints?.[JOINT_NAMES[i]];
        if (tj) {
          if (tj.visible) {
            const p = tj.position;
            pos.x = p.x; pos.y = p.y; pos.z = p.z;
            validCount++;
          }
          // Invisible = no pose this frame: keep the previous position (below).
          continue;
        }
        const jointSpace = xrHand.get(JOINT_NAMES[i]);
        if (jointSpace) {
          const pose = frame.getJointPose?.(jointSpace, refSpace);
          if (pose) {
            const p = pose.transform.position;
            pos.x = p.x; pos.y = p.y; pos.z = p.z;
            validCount++;
            continue;
          }
        }
        // Missing joint this frame: KEEP the previous position. Collapsing it
        // to the origin would fabricate a pinch/grab (tip-to-tip distance 0)
        // and the gesture recognizer would fire a false press.
      }

      // Whole hand lost tracking this frame (no joint resolved): leave
      // leftJoints/rightJoints null so the tracking-loss path below force-
      // releases held gestures instead of feeding stale joints to the recognizer.
      if (validCount === 0) continue;

      if (isLeft) leftJoints = joints;
      else rightJoints = joints;
    }

    // Clean up stale prev-state for hands that disappeared mid-session
    // (controller disconnected while session is still active).
    // Emit synthetic releases for any buttons that were held at disconnect.
    if (!seenLeft && this._prevXRButtons.has('left')) {
      _flushXRReleases(this._prevXRButtons.get('left')!, 'Left', reader);
      this._prevXRButtons.delete('left');
    }
    if (!seenRight && this._prevXRButtons.has('right')) {
      _flushXRReleases(this._prevXRButtons.get('right')!, 'Right', reader);
      this._prevXRButtons.delete('right');
    }

    // ─── Hand Gesture Recognition ─────────────────────────────────────────────
    // Detect per-hand tracking loss and force-release stuck gestures
    for (let h = 0; h < 2; h++) {
      const isTracked = h === 0 ? leftJoints !== null : rightJoints !== null;
      if (this._handWasTracked[h] && !isTracked) {
        this.gestureRecognizer.forceReleaseHand(h as 0 | 1, reader);
      }
      this._handWasTracked[h] = isTracked;
    }

    this.gestureRecognizer.update(leftJoints, rightJoints, reader);

    // ─── Vision Pro Transient-Pointer ─────────────────────────────────────────
    this.visionProInput.update(session, frame, refSpace, reader);
    // Sticky: once a transient-pointer is seen, flag persists until session ends
    // so DeviceDetector's 2-frame hysteresis can latch on VisionProGaze.
    if (this.visionProInput.isTransientPointerActive()) {
      this._visionProDetected = true;
    }
    if (typeof reader._setVisionProActive === 'function') {
      reader._setVisionProActive(this._visionProDetected);
    }
  }

  /** Press/release edge detection for one xr-standard button (no per-frame closure). */
  private _edgeDetect(reader: any, prev: boolean[], gp: Gamepad, btnIndex: number, actionName: string): void {
    const nowPressed = gp.buttons[btnIndex]?.pressed ?? false;
    const wasPressed = prev[btnIndex];
    if (nowPressed && !wasPressed) {
      reader._setPressed(actionName);
    } else if (!nowPressed && wasPressed) {
      reader._setReleased(actionName);
    }
    prev[btnIndex] = nowPressed;
  }

  private _findInputSystem(world: World): any {
    // Trust the cached reference. Systems aren't hot-swapped mid-session in
    // EngineX, so validating via `systems.includes(cache)` every frame
    // (called twice per execute at 90 Hz) is pure linear-scan overhead.
    // Callers that genuinely tear down an InputSystem at runtime should
    // call `dispose()` to null the cache.
    if (this._inputSystemCache) return this._inputSystemCache;
    const systems = (world as any).scheduler?.systems as any[] | undefined;
    if (!systems) return null;
    const found = systems.find((s: any) => s.name === 'Input');
    if (found) this._inputSystemCache = found;
    return found;
  }

  /** Clear cached InputSystem reference — call if the world's systems change. */
  dispose(): void {
    this._inputSystemCache = null;
  }
}
